import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";

import Logo from "@/components/common/Logo";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import GridBackground from "@/components/common/GridBackground";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiError } from "@/lib/api";

export default function LoginPage() {
  const { user, login } = useAuth();
  const { t } = useLanguage();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && typeof user === "object") return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      {/* Left — brand panel */}
      <div className="hidden lg:flex relative border-r border-border overflow-hidden">
        <GridBackground />
        <div className="relative z-10 w-full flex flex-col justify-between p-10">
          <Link to="/"><Logo size={32} /></Link>
          <div className="max-w-md">
            <div className="text-overline mb-5">Sector · Command Center</div>
            <h2 className="text-4xl font-semibold tracking-tight text-white leading-tight">
              Secure the integration chain — from Santiago to Montreal.
            </h2>
            <p className="mt-6 text-sm text-secondary-foreground/70 leading-relaxed">
              Project visibility, AI-powered multilingual ticket triage, firmware compliance and white-labeled commissioning reports for your entire Kantech · Avigilon · Lenel · Magos estate.
            </p>
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">
            <span>ZERO-FOOTPRINT WHITE LABEL</span>
            <span>v1.0</span>
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex flex-col">
        <div className="px-6 lg:px-10 h-16 flex items-center justify-between border-b border-border">
          <Link to="/" className="flex items-center gap-2 text-xs font-mono tracking-widest text-muted-foreground hover:text-primary transition-colors" data-testid="login-back-home">
            <ArrowLeft className="w-3 h-3" /> HOME
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 lg:px-10 py-10">
          <div className="w-full max-w-md">
            <div className="text-overline mb-3">SECURE ACCESS</div>
            <h1 className="text-3xl font-semibold text-white tracking-tight" data-testid="login-title">{t("auth.login.title")}</h1>
            <p className="mt-3 text-sm text-secondary-foreground/70">{t("auth.login.subtitle")}</p>

            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
              <Field label={t("auth.email")} testid="login-email">
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input" autoComplete="email"
                  className="w-full h-11 px-4 rounded-sm bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none font-mono text-sm tracking-wide text-white"
                />
              </Field>
              <Field label={t("auth.password")} testid="login-password">
                <input
                  type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input" autoComplete="current-password"
                  className="w-full h-11 px-4 rounded-sm bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none font-mono text-sm tracking-wide text-white"
                />
              </Field>
              <button
                type="submit" disabled={busy} data-testid="login-submit-button"
                className="w-full h-11 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors disabled:opacity-50 glow-blue inline-flex items-center justify-center gap-2"
              >
                {busy ? "…" : t("auth.signin")} {!busy && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>

            <div className="my-8 flex items-center gap-3 text-[10px] font-mono tracking-[0.24em] text-muted-foreground uppercase">
              <div className="flex-1 divider-line" />
              <span>OR</span>
              <div className="flex-1 divider-line" />
            </div>

            <button
              onClick={loginWithGoogle} data-testid="login-google-button"
              className="w-full h-11 rounded-sm border border-border hover:border-primary/70 hover:bg-secondary/60 text-sm text-white transition-colors inline-flex items-center justify-center gap-3"
            >
              <GoogleMark /> {t("auth.google")}
            </button>

            <div className="mt-10 text-sm text-secondary-foreground/70">
              {t("auth.noaccount")}{" "}
              <Link to="/register" className="text-primary link-underline" data-testid="login-register-link">{t("auth.register")}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={`${testid}-field`}>
      <div className="mb-2 text-[10px] font-mono tracking-[0.24em] text-muted-foreground uppercase">{label}</div>
      {children}
    </label>
  );
}

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285f4" d="M21.805 10.023h-9.765v3.977h5.632c-.244 1.278-1.474 3.75-5.632 3.75-3.388 0-6.15-2.805-6.15-6.25s2.762-6.25 6.15-6.25c1.929 0 3.223.822 3.962 1.527l2.706-2.611C16.99 2.586 14.745 1.5 12.04 1.5 6.77 1.5 2.5 5.77 2.5 11.039c0 5.269 4.27 9.54 9.54 9.54 5.508 0 9.155-3.87 9.155-9.32 0-.625-.074-1.105-.174-1.577l-.216-.659Z" /></svg>
  );
}
